import type * as A from '@/api';
import { logger } from '@/utils/logger';
import { graphql } from '@/utils/graphql';
import { handleSystemError } from '@/utils/handleError';
import type { PartialBy } from './types';
import type Dataset from './dataset';
import { assertIsDefined } from '@/utils/typeChecks';

type FolderConstructorInput = A.GetFolderQuery['getFolder'] & {
  isSubFoldersFetched: boolean;
  isDatasetsFetched: boolean;
  // only fetched by listFoldersByParent; used to pick the oldest root folder (BEEM-2182)
  createdAt?: string;
};

type FolderCreateInput = PartialBy<A.GetFolderQuery['getFolder'], 'id'>;

type FolderUpdateInput = Partial<A.GetFolderQuery['getFolder']>;

export default class Folder {
  readonly id: string;

  readonly name: string;

  // parentId can be ID of the parent folder or the ID of the workspace when it's the root "Hub" folder
  readonly parentId: string;

  // parent can be undefined when the folder is the root "Hub" folder
  readonly parent?: Pick<Folder, 'id' | 'name'>;

  // undefined when the record predates timestamps or was not fetched by the query
  readonly createdAt?: string;

  #subFolders?: Pick<Folder, 'id' | 'name'>[];
  get subFolders(): Pick<Folder, 'id' | 'name'>[] {
    assertIsDefined(this.#subFolders, 'BEEM250221140606');
    return this.#subFolders;
  }

  // this property is useful, among other reasons, to quickly know the dataset count in the folder, before deleting it
  #datasets?: Pick<Dataset, 'id' | 'name'>[];
  get datasets(): Pick<Dataset, 'id' | 'name'>[] {
    assertIsDefined(this.#datasets, 'BEEM250221140607');
    return this.#datasets;
  }

  private constructor(input: FolderConstructorInput) {
    this.id = input.id;
    this.name = input.name;
    this.parentId = input.parentId;
    this.parent = input.parent;
    this.createdAt = input.createdAt;
    this.#subFolders = input.isSubFoldersFetched ? input.subFolders?.items ?? [] : undefined;
    this.#datasets = input.isDatasetsFetched ? input.datasets?.items ?? [] : undefined;

    this.update = this.update.bind(this);
  }

  async update(input: FolderUpdateInput): Promise<void> {
    logger.info({ label: 'app.services.folder.update.input', message: input });
    await graphql('customUpdateFolder', { input: { id: this.id, ...input } });
    Object.entries(input).forEach(([k, v]) => {
      // @ts-expect-error this is ok because there's no harm in setting a value for a prop if it doesn't exist, maybe in the future TS will handle this better
      this[k] = v;
    });
  }

  static async getFolderById(id: string): Promise<Folder> {
    logger.info(id, { label: 'app.services.folder.getFolderById.id' });
    const data = await graphql('getFolder', { id });
    return new Folder({ ...data, isSubFoldersFetched: true, isDatasetsFetched: true });
  }

  static async getFoldersByParentId(parentId: string): Promise<Folder[]> {
    logger.info(parentId, { label: 'app.services.folder.getFoldersByParentId.parentId' });
    const data = await graphql('listFoldersByParent', { parentId });
    return data.items.map(
      (el) => new Folder({ ...el, isSubFoldersFetched: false, isDatasetsFetched: false }),
    );
  }

  static async getRootFolder(workspaceId: string): Promise<Folder> {
    logger.info(workspaceId, { label: 'app.services.folder.getRootFolder.workspaceId' });
    const rs = await Folder.getFoldersByParentId(workspaceId);
    logger.debug({ label: 'app.services.folder.getRootFolder.rs', message: rs });
    if (!rs || rs.length === 0)
      throw new Error('Folder - getRootFolder - invalid condition - no root folder found');
    if (rs.length > 1) {
      // BEEM-2182 / BTS-790: a workspace must have exactly one root folder. The GraphQL layer
      // now enforces this, but an anomalous extra root (e.g. from a direct DynamoDB edit) must
      // not prevent the app from loading: report the anomaly and use the oldest root, which is
      // the original one. Folders without createdAt were not created through the API and lose.
      handleSystemError(
        new Error(
          `Folder - getRootFolder - workspace ${workspaceId} has ${rs.length} root folders: ${rs
            .map((el) => el.id)
            .join(', ')}`,
        ),
        'app.services.folder.getRootFolder.multipleRootFolders',
      );
      const NO_CREATED_AT_SENTINEL = '9999-12-31T23:59:59.999Z';
      const createdKey = (el: Folder) => el.createdAt ?? NO_CREATED_AT_SENTINEL;
      return [...rs].sort(
        (a, b) => createdKey(a).localeCompare(createdKey(b)) || a.id.localeCompare(b.id),
      )[0];
    }
    return rs[0];
  }

  static async create(input: FolderCreateInput): Promise<string> {
    logger.info({ label: 'app.services.folder.create.input', message: input });
    const data = await graphql('customCreateFolder', { input });
    return data.id;
  }

  /**
   * The folder at `names` under `parentId`, each level found by name (case-insensitively) or
   * created: where the AI Assistant files what it creates (AI Assistant > user > conversation).
   */
  static async ensurePath(parentId: string, names: string[]): Promise<string> {
    // One level after the other: each is looked up in the one above it.
    return names.reduce<Promise<string>>(async (above, raw) => {
      const current = await above;
      const name = raw.trim().slice(0, 120) || 'Untitled';
      const parent = await Folder.getFolderById(current);
      const existing = parent.subFolders.find(
        (f) => f.name.trim().toLowerCase() === name.toLowerCase(),
      );
      return existing ? existing.id : Folder.create({ name, parentId: current });
    }, Promise.resolve(parentId));
  }

  static async delete(id: string): Promise<void> {
    logger.info(id, { label: 'app.services.folder.delete.id' });

    // make sure it's empty
    const folder = await Folder.getFolderById(id);
    const subFolderCount = folder.subFolders.length;
    const datasetCount = folder.datasets.length;

    logger.debug({ label: 'app.services.folder.delete.subFolderCount', message: subFolderCount });
    logger.debug({ label: 'app.services.folder.delete.datasetCount', message: datasetCount });

    if (subFolderCount > 0 || datasetCount > 0)
      // vinhf want to create a class for BeemError which always have "code" and "message" and "stack trace"
      throw new Error(
        `FOLDER_NOT_EMPTY - Folder is not empty: ${subFolderCount} sub-folders, ${datasetCount} datasets. Please empty before deleting it.`,
      );

    await graphql('customDeleteFolder', { input: { id } });
  }
}
